import { AfterContentChecked, Component, ErrorHandler, Inject, OnDestroy, OnInit, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { email, FieldTree, form, required, validate } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'

import { LaskuSharedService } from '@lemon/core-firebase/service/lasku/lasku-shared.service'
import { LaskuSpostiService } from '@lemon/core-firebase/service/lasku/lasku-sposti.service'

import { arvonMuutokset, asetaArvo } from '../../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FieldErrors, FormValidationService } from '../../_jaettu-angular/service/form-validation.service'
import { LadataanService } from '../../_jaettu-angular/service/ladataan.service'
import { TimestampService } from '../../_jaettu-angular/service/timestamp-service'

import { Asiakas } from '../../_jaettu-lemonator/model/asiakas'
import { Kirjanpitaja } from '../../_jaettu-lemonator/model/kirjanpitaja'

import { LaskuService } from '../../_angular/service/lasku/lasku.service'

import { LaskuEmailEsikatselutiedot } from '../../_jaettu-angular/laskut/esikatselu/email.perinteinen.component'

import { EmailLahetysStatus, EmailLahetysStatusKoodi, Lasku, LaskuBase, Laskuasetukset, LaskuasetuksetSpostille, LaskunSahkpostipohjanVari, LaskunumeroTyyppi } from '@lemon/core-firebase/model/lasku'

import { ReplaySubject, Subject } from 'rxjs'
import { auditTime, takeUntil } from 'rxjs/operators'

interface SpostiLomake {
  aihe: string
  otsikko: string
  teksti: string
  lisaavastaanottaja: string
}

export interface LaskuSpostiData {
  asiakas: Asiakas
  kirjanpitaja: Kirjanpitaja
  juurilasku: Lasku
  kasiteltava: LaskuBase
  asetukset: Laskuasetukset
}

@Component({
  templateUrl: './lasku.sposti.dialog.html',
  styleUrls: ['./lasku.sposti.dialog.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LaskuSpostiDialog implements OnInit, AfterContentChecked, OnDestroy {
  private spostiasetukset: LaskuasetuksetSpostille = null
  private tilaaEsikatseluPaivitysSubject: ReplaySubject<number> = new ReplaySubject(1)
  private ngUnsubscribe = new Subject<void>()
  private readonly _injector = inject(Injector)
  private readonly _vastaanottajatPuuttuvat = signal(false)

  esikatselutiedot: ReplaySubject<LaskuEmailEsikatselutiedot> = new ReplaySubject(1)
  lomake: FieldTree<SpostiLomake>
  asetuksissaOnOletustietojaVirhe = false
  fieldErrors: FieldErrors = {}

  email = {
    aihe: '',
    otsikko: '',
    teksti: '',
    vastaanottajat: [],
  }

  varit: { [key in LaskunSahkpostipohjanVari]: string } = {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    header_taustavari: 'rgb(250,239,89)',
    valiviiva: 'rgb(46,204,255)',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    header_fontti: 'rgb(0,0,0)',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    otsikko_fontti: 'rgb(0,0,0)',
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: LaskuSpostiData,
    private dialogRef: MatDialogRef<LaskuSpostiDialog>,
    private validationService: FormValidationService,
    private laskuSpostiService: LaskuSpostiService,
    private laskuService: LaskuService,
    private errorHandler: ErrorHandler,
    private shared: LaskuSharedService,
    private ladataanService: LadataanService,
    private timestampService: TimestampService,
  ) {
    if (!data.asetukset.spostiasetukset) {
      data.asetukset.spostiasetukset = {}
    }

    this.spostiasetukset = this.laskuSpostiService.annaSpostiAsetukset(data.kasiteltava, false, data.asetukset)

    const oletuspohja = data.kasiteltava.nrotyyppi === LaskunumeroTyyppi.HYVITYS ? 'hyvityssposti' : 'sposti'
    this.email.aihe = this.korvaaMuuttujat(this.spostiasetukset.aihe || this.shared.annaLokalisoituMerkkijono(`lasku.spostipohjat.oletukset.${oletuspohja}.aihe`, data.kasiteltava))
    this.email.otsikko = this.korvaaMuuttujat(this.spostiasetukset.otsikko || this.shared.annaLokalisoituMerkkijono(`lasku.spostipohjat.oletukset.${oletuspohja}.otsikko`, data.kasiteltava))
    this.email.teksti = this.korvaaMuuttujat(this.spostiasetukset.teksti || this.shared.annaLokalisoituMerkkijono(`lasku.spostipohjat.oletukset.${oletuspohja}.teksti`, data.kasiteltava))

    if (data.kasiteltava.asiakas.laskunVastaanottajat) {
      for (const vastaanottaja of data.kasiteltava.asiakas.laskunVastaanottajat) {
        this.email.vastaanottajat.push(vastaanottaja)
      }
    }

    if (!data.kasiteltava.email) {
      data.kasiteltava.email = {
        start: this.timestampService.now(),
        done: null,
        aihe: '',
        otsikko: '',
        teksti: '',
        slogan: '',
        template: null,
        vastaanottajat: null,
      }
    }
    data.kasiteltava.email.aihe = this.email.aihe
    data.kasiteltava.email.otsikko = this.email.otsikko
    data.kasiteltava.email.teksti = this.email.teksti
    data.kasiteltava.email.vastaanottajat = this.muodostaVastaanottaja()
    data.kasiteltava.email.replyTo = this.data.kirjanpitaja.email
    data.kasiteltava.email.footer = 'footer1'
    data.kasiteltava.email.logoInFooter = true
  }

  ngAfterContentChecked() {
    this.fieldErrors = this.validationService.updateValidationStatus('lasku.sposti', this.lomake)
  }

  ngOnInit() {
    this.lomake = form(
      signal<SpostiLomake>({ aihe: this.email.aihe, otsikko: this.email.otsikko, teksti: this.email.teksti, lisaavastaanottaja: '' }),
      polku => {
        required(polku.aihe)
        required(polku.otsikko)
        required(polku.teksti)
        // Reaktiivisen lomakkeen setErrors korvasi kentän muut virheet, kunnes kenttä validoitiin uudelleen
        email(polku.lisaavastaanottaja, { when: ({ value }) => !this._vastaanottajatPuuttuvat() && !!value()?.trim() })
        validate(polku.lisaavastaanottaja, () => (this._vastaanottajatPuuttuvat() ? { kind: 'min' } : null))
      },
      { injector: this._injector },
    )
    const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta, { injector: this._injector })

    muutokset(this.lomake.aihe).subscribe(value => {
      this.email.aihe = value || ''
      this.data.kasiteltava.email.aihe = this.email.aihe
      this.tilaaEsikatseluPaivitysSubject.next(1)
    })
    muutokset(this.lomake.otsikko).subscribe(value => {
      this.email.otsikko = value || ''
      this.data.kasiteltava.email.otsikko = this.email.otsikko
      this.tilaaEsikatseluPaivitysSubject.next(1)
    })
    muutokset(this.lomake.teksti).subscribe(value => {
      this.email.teksti = value || ''
      this.data.kasiteltava.email.teksti = this.email.teksti
      this.tilaaEsikatseluPaivitysSubject.next(1)
    })
    muutokset(this.lomake.lisaavastaanottaja).subscribe(value => {
      this._vastaanottajatPuuttuvat.set(false)
      if (value && (value.indexOf(',') !== -1 || value.indexOf(' ') !== -1)) {
        this.lisaaEmail(this.lomake.lisaavastaanottaja)
      }
    })

    this.tilaaEsikatseluPaivitysSubject.pipe(takeUntil(this.ngUnsubscribe), auditTime(250)).subscribe(() => {
      if (this.data.asetukset && this.data.juurilasku && this.data.kasiteltava) {
        this.esikatselutiedot.next({
          asetukset: this.data.asetukset,
          juurilasku: this.data.juurilasku,
          kasiteltava: this.data.kasiteltava,
          maksumuistutus: false,
        })
      }
    })

    this.tilaaEsikatseluPaivitysSubject.next(1)
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  lisaa() {
    this._vastaanottajatPuuttuvat.set(false)
    this.lisaaEmail(this.lomake.lisaavastaanottaja)
  }

  private lisaaEmail(field: FieldTree<string>) {
    if (!field().valid()) {
      return
    }
    const value = field().value()
    if ((value || '').trim()) {
      let arvo = value.trim()
      arvo = arvo.replace(/,/g, '').trim()
      this.email.vastaanottajat.push(arvo)
    }
    asetaArvo(field, '')
    this.data.kasiteltava.email.vastaanottajat = this.muodostaVastaanottaja()
    this.tilaaEsikatseluPaivitysSubject.next(1)
  }

  poistaAsiakas(vastaanottaja: string): void {
    const index = this.email.vastaanottajat.indexOf(vastaanottaja)
    if (index >= 0) {
      this.email.vastaanottajat.splice(index, 1)
    }
    this.data.kasiteltava.email.vastaanottajat = this.muodostaVastaanottaja()
    this.tilaaEsikatseluPaivitysSubject.next(1)
  }

  private muodostaVastaanottaja() {
    const vastaanottajat: EmailLahetysStatus[] = []
    for (const vastaanottaja of this.email.vastaanottajat) {
      vastaanottajat.push({
        email: vastaanottaja.toLowerCase(),
        status: EmailLahetysStatusKoodi.PROSESSOIDAAN,
        viesti: null,
      })
    }
    return vastaanottajat
  }

  send() {
    if (!this.email || !this.email.vastaanottajat || this.email.vastaanottajat.length < 1) {
      this._vastaanottajatPuuttuvat.set(true)
      return
    }

    // if (this.asetustenOletuksetService.tarkistaOnkoAsetuksetOletuksia(this.data.asetukset)) {
    //   this.asetuksissaOnOletustietojaVirhe = true
    //   return
    // }

    this.ladataanService.aloitaLataaminen()
    const vastaanottajat: EmailLahetysStatus[] = this.muodostaVastaanottaja()

    // Lemonatorista lähettäessä käytetään aina "perinteinen-lasku" pohjia (oma layout ja värit)
    const postmarkTemplateId = this.data.kasiteltava.nrotyyppi === LaskunumeroTyyppi.HYVITYS ? LaskuSpostiService.HYVITYS_OLETUSTEMPLATE : LaskuSpostiService.OLETUSTEMPLATE
    this.laskuService
      .merkitseLaskuLahetetyksiSpostilla(
        this.data.asiakas,
        this.data.kirjanpitaja,
        postmarkTemplateId,
        this.data.juurilasku,
        this.data.kasiteltava,
        vastaanottajat,
        this.email.aihe,
        this.email.otsikko,
        this.email.teksti,
        this.data.asetukset.slogan,
        this.varit,
      )
      .then(result => {
        this.ladataanService.lopetaLataaminen()
        this.dialogRef.close(true)
      })
      .catch(err => {
        this.ladataanService.lopetaLataaminen()
        this.errorHandler.handleError(err)
      })
  }

  private korvaaMuuttujat(korvattava: string): string {
    return this.laskuSpostiService.korvaaMuuttujat(korvattava, this.data.asetukset, this.data.juurilasku, this.data.kasiteltava)
  }
}
